import React, { useRef, useEffect, useState, useCallback } from 'react';
import { findSnapPointWithPolygons, getDistance } from '../../utils/snapping';
import { ZoomIn, ZoomOut, Maximize2, PenTool } from 'lucide-react';
import {
    getUsablePolygons,
    moduleFitsUsableArea,
    pointerToModuleAngle,
    normalizeAngle360,
    snapAngleWithStillThreshold,
    snapAngleToEdges,
    getSnapEdgeAngles,
    getUsableEdgeLabels,
    hitTestM2Modules,
    hitTestInfraModules,
    hitTestSubAreas,
    hitTestCutLines,
    hitTestCutLineNode,
    hitTestPlacedModules,
    hitTestHatchModules,
    hitTestResultM1Modules,
    hitTestGroupOutlines,
    hitTestCampusGroup,
    campusRigidFits,
    formatHatchModuleId,
    formatResultM1ModuleId,
    formatResultM2ModuleId,
    formatGroupSelectionId,
    groupClusterCentroid,
    getPlacedModuleDimensions,
    updateAreaSelection,
    SUB_AREA_COLORS,
    SUB_AREA_STROKES,
    getManualHatchModules,
    formatAreaLabel,
    getPolygonCentroid,
    getModuleSnapExcludePolygons,
    getHatchStepAfterOrigin,
    computeCorridorDisplayLines,
    isDivisionBorderPoint,
    pointsNearEachOther,
    findBoundaryEdgeInsertion,
} from './sitePlannerGeometry';

const PLACED_MODULE_STYLES = {
    M1: { fill: 'rgba(52, 211, 153, 0.4)', stroke: '#34d399' },
    M2: { fill: 'rgba(236, 72, 153, 0.4)', stroke: '#ec4899' },
    M3: { fill: 'rgba(251, 191, 36, 0.4)', stroke: '#fbbf24' },
    M4: { fill: 'rgba(96, 165, 250, 0.4)', stroke: '#60a5fa' },
};

export default function DXFViewport({
    dxfData,
    visibleLayers,
    boundary,
    setBoundary,
    setback,
    results,
    m1Params,
    m2Params,
    m3Params,
    m4Params,
    module3Positions = [],
    module4Positions = [],
    beginUndoableEdit,
    onM2Commit,
    onM3Commit,
    onM4Commit,
    onInfraPlace,
    manualMode,
    toolMode,
    cutLines,
    corridorWallLines,
    subAreas,
    hasSplitSubAreas = false,
    selectedAreaIds,
    setSelectedAreaIds,
    areaHatchConfigs,
    updateAreaHatchConfig,
    hatchAngleStep,
    setHatchAngleStep,
    setToolMode,
    setError,
    areaLayouts,
    placedModules,
    selectedCutLineId,
    setSelectedCutLineId,
    selectedModuleId,
    setSelectedModuleId,
    onCommitDivisionPolyline,
    onCommitCorridor,
    corridorWidth,
    onUpdateCutLine,
    onPlaceModule,
    onUpdatePlacedModule,
    onUpdateHatchModule,
    onUpdateResultM1,
    onTranslateGroup,
    onTranslateCluster,
    onRefitCampus,
    onDeletePlacedModule,
    clearSelection,
    onManualModeChange,
    paletteDragType,
    setPaletteDragType,
}) {
    const canvasRef = useRef(null);
    const containerRef = useRef(null);
    const rotationInputRef = useRef(null);
    const pointerStillSinceRef = useRef(Date.now());
    const lastPointerRef = useRef(null);
    const mousePosRef = useRef({ x: 0, y: 0 });
    const snapInfoRef = useRef(null);

    const [scale, setScale] = useState(1);
    const [offset, setOffset] = useState({ x: 50, y: 50 });
    const [isPanning, setIsPanning] = useState(false);
    const [panStart, setPanStart] = useState({ x: 0, y: 0 });
    const [touchStartDist, setTouchStartDist] = useState(null);
    const [touchStartScale, setTouchStartScale] = useState(1);

    const [draggedVertex, setDraggedVertex] = useState(null);
    const [draggedM2, setDraggedM2] = useState(null);
    const [draftM2List, setDraftM2List] = useState(null);
    const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
    const [dxfMousePos, setDxfMousePos] = useState([0, 0]);
    const [snapInfo, setSnapInfo] = useState(null);
    const [contextMenu, setContextMenu] = useState(null);
    const [rotation, setRotation] = useState(null);
    const [invalidM2Preview, setInvalidM2Preview] = useState(null);
    const [splitDraftPoints, setSplitDraftPoints] = useState([]);
    const [corridorDraft, setCorridorDraft] = useState([]);
    const [draggedCutNode, setDraggedCutNode] = useState(null);
    const [cutLineDragDraft, setCutLineDragDraft] = useState(null);
    const [draggedPlacedModule, setDraggedPlacedModule] = useState(null);
    const [draggedHatchModule, setDraggedHatchModule] = useState(null);
    const [draggedResultM1, setDraggedResultM1] = useState(null);
    const groupDragRef = useRef(null);
    const clusterDragRef = useRef(null);
    const [moduleRotation, setModuleRotation] = useState(null);
    const [hatchAnglePreview, setHatchAnglePreview] = useState(null);

    const snapEdgeAngles = getSnapEdgeAngles(boundary, subAreas, results);

    const m2Len = m2Params.length || 16.0;
    const m2Wid = m2Params.width || 2.6;
    const m3Len = m3Params?.length || 8.0;
    const m3Wid = m3Params?.width || 3.0;
    const m4Len = m4Params?.length || 6.0;
    const m4Wid = m4Params?.width || 2.5;
    const usablePolygons = getUsablePolygons(results);
    const displayM2 = draftM2List ?? results?.module2;
    const manualModules = getManualHatchModules(areaLayouts);
    const activeHatchAreaId = selectedAreaIds?.length === 1 ? selectedAreaIds[0] : null;
    const activeHatch = activeHatchAreaId ? areaHatchConfigs[activeHatchAreaId] : null;

    const moduleSnapExclude = getModuleSnapExcludePolygons(
        placedModules,
        areaLayouts,
        results,
        m1Params,
        m2Params,
    );
    const snapForModules = !(paletteDragType || draggedPlacedModule || draggedHatchModule || draggedResultM1 != null);

    const saveSnappedHatchAngle = useCallback(
        (areaId, rawAngle, angleRef) => {
            const snapped = snapAngleToEdges(rawAngle, snapEdgeAngles, 2);
            updateAreaHatchConfig(areaId, {
                angle: normalizeAngle360(snapped),
                angleRef: angleRef || undefined,
            });
        },
        [snapEdgeAngles, updateAreaHatchConfig],
    );

    const getSelectedCutLine = () =>
        (cutLines || []).find((l) => l.id === selectedCutLineId) || null;

    const displayCutLines = [
        ...(cutLines || []).map((line) => {
            if (cutLineDragDraft?.lineId === line.id) {
                return { ...line, p1: cutLineDragDraft.p1, p2: cutLineDragDraft.p2 };
            }
            return line;
        }),
        ...(corridorWallLines || []),
    ];

    const snapPolygons = [
        ...(boundary.length >= 2 ? [boundary] : []),
        ...usablePolygons,
        ...(subAreas || []).map((a) => a.boundary).filter(Boolean),
    ];

    const corridorBorderPolygons = snapPolygons.filter((poly) => poly?.length >= 2);

    const getCorridorPreviewCenterline = useCallback(
        (includeCursor) => {
            if (!corridorDraft.length) return [];
            if (!includeCursor || corridorDraft.length < 1) return corridorDraft;
            const cursor = snapInfoRef.current?.point || dxfMousePos;
            return [...corridorDraft, cursor];
        },
        [corridorDraft, dxfMousePos],
    );

    const getSnapPoint = useCallback(
        (mx, my) => {
            if (!snapForModules) return null;
            const snapRadius = 15 / scale;
            return findSnapPointWithPolygons(
                [mx, my],
                dxfData?.layers,
                visibleLayers,
                snapRadius,
                snapPolygons,
                moduleSnapExclude,
            );
        },
        [scale, dxfData, visibleLayers, snapPolygons, snapForModules, moduleSnapExclude],
    );

    const bounds = dxfData?.bounds || { min_x: 0, min_y: 0, max_x: 100, max_y: 100 };
    const dx = bounds.max_x - bounds.min_x || 100;
    const dy = bounds.max_y - bounds.min_y || 100;

    const resetView = () => {
        if (!canvasRef.current) return;
        const canvas = canvasRef.current;
        const pad = 40;
        const scaleX = (canvas.width - pad * 2) / dx;
        const scaleY = (canvas.height - pad * 2) / dy;
        const newScale = Math.min(scaleX, scaleY);
        setScale(newScale);
        const centerX = (bounds.min_x + bounds.max_x) / 2;
        const centerY = (bounds.min_y + bounds.max_y) / 2;
        setOffset({
            x: canvas.width / 2 - centerX * newScale,
            y: canvas.height / 2 + centerY * newScale,
        });
    };

    useEffect(() => {
        resetView();
    }, [dxfData]);

    const toScreen = (x, y) => {
        const sx = x * scale + offset.x;
        const sy = offset.y - y * scale;
        return [sx, sy];
    };

    const toDxf = (sx, sy) => {
        const x = (sx - offset.x) / scale;
        const y = (offset.y - sy) / scale;
        return [x, y];
    };

    const drawOrientedBox = (ctx, cx, cy, length, width, angleDeg, style = {}) => {
        const rad = (angleDeg * Math.PI) / 180.0;
        const c = Math.cos(rad);
        const s = Math.sin(rad);
        const halfL = length / 2;
        const halfW = width / 2;
        const corners = [
            [-halfL, -halfW],
            [halfL, -halfW],
            [halfL, halfW],
            [-halfL, halfW],
        ];

        ctx.beginPath();
        corners.forEach((pt, idx) => {
            const rx = pt[0] * c - pt[1] * s;
            const ry = pt[0] * s + pt[1] * c;
            const scr = toScreen(cx + rx, cy + ry);
            if (idx === 0) ctx.moveTo(scr[0], scr[1]);
            else ctx.lineTo(scr[0], scr[1]);
        });
        ctx.closePath();
        ctx.fillStyle = style.fill || 'rgba(236, 72, 153, 0.35)';
        ctx.strokeStyle = style.stroke || '#ec4899';
        ctx.lineWidth = style.lineWidth || 1.5;
        ctx.fill();
        ctx.stroke();
    };

    const drawPolygon = (ctx, polygon, style) => {
        if (!polygon || polygon.length < 2) return;
        ctx.beginPath();
        const start = toScreen(polygon[0][0], polygon[0][1]);
        ctx.moveTo(start[0], start[1]);
        for (let i = 1; i < polygon.length; i++) {
            const pt = toScreen(polygon[i][0], polygon[i][1]);
            ctx.lineTo(pt[0], pt[1]);
        }
        ctx.closePath();
        if (style.fill) {
            ctx.fillStyle = style.fill;
            ctx.fill();
        }
        ctx.strokeStyle = style.stroke;
        ctx.lineWidth = style.lineWidth || 1;
        ctx.setLineDash(style.dash || []);
        ctx.stroke();
        ctx.setLineDash([]);
    };

    const draw = useCallback(() => {
        if (!canvasRef.current) return;
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');

        ctx.fillStyle = '#0f172a';
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        if (dxfData?.layers) {
            ctx.lineWidth = 1;
            for (const layerName of visibleLayers) {
                const entities = dxfData.layers[layerName] || [];
                ctx.strokeStyle = 'rgba(148, 163, 184, 0.15)';
                for (const ent of entities) {
                    if (!ent.points || ent.points.length < 2) continue;
                    ctx.beginPath();
                    const start = toScreen(ent.points[0][0], ent.points[0][1]);
                    ctx.moveTo(start[0], start[1]);
                    for (let i = 1; i < ent.points.length; i++) {
                        const pt = toScreen(ent.points[i][0], ent.points[i][1]);
                        ctx.lineTo(pt[0], pt[1]);
                    }
                    if (ent.closed) ctx.closePath();
                    ctx.stroke();
                }
            }
        }

        if (hasSplitSubAreas) {
            usablePolygons.forEach((poly) => {
                drawPolygon(ctx, poly, {
                    fill: 'rgba(251, 191, 36, 0.04)',
                    stroke: 'rgba(251, 191, 36, 0.55)',
                    lineWidth: 1.5,
                    dash: [6, 4],
                });
            });
        }

        (subAreas || []).forEach((area, idx) => {
            const isSelected = selectedAreaIds?.includes(area.id);
            const isCorridor = area.is_corridor;
            const isEnclosure = area.is_enclosure;
            const colorIdx = idx % SUB_AREA_COLORS.length;
            drawPolygon(ctx, area.boundary, {
                fill: isCorridor
                    ? (isSelected ? 'rgba(251, 146, 60, 0.28)' : 'rgba(251, 146, 60, 0.14)')
                    : isEnclosure
                      ? (isSelected ? 'rgba(56, 189, 248, 0.28)' : 'rgba(56, 189, 248, 0.14)')
                      : (isSelected ? 'rgba(167, 139, 250, 0.22)' : SUB_AREA_COLORS[colorIdx]),
                stroke: isCorridor
                    ? (isSelected ? '#fdba74' : '#fb923c')
                    : isEnclosure
                      ? (isSelected ? '#7dd3fc' : '#38bdf8')
                      : (isSelected ? '#c4b5fd' : SUB_AREA_STROKES[colorIdx]),
                lineWidth: isSelected ? 2.5 : 1.5,
            });

            if (area.boundary?.length >= 3) {
                const [cx, cy] = getPolygonCentroid(area.boundary);
                const scr = toScreen(cx, cy);
                const label = formatAreaLabel(idx);
                ctx.font = 'bold 14px sans-serif';
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';
                ctx.fillStyle = 'rgba(15, 23, 42, 0.75)';
                ctx.fillRect(scr[0] - 16, scr[1] - 11, 32, 22);
                ctx.fillStyle = isSelected ? '#e9d5ff' : '#c4b5fd';
                ctx.fillText(label, scr[0], scr[1]);
            }
        });

        (displayCutLines || []).forEach((line) => {
            if (!line.p1 || !line.p2) return;
            const isCorridorWall = line.source === 'corridor';
            const isSelected = !isCorridorWall && line.id === selectedCutLineId;
            const s1 = toScreen(line.p1[0], line.p1[1]);
            const s2 = toScreen(line.p2[0], line.p2[1]);
            ctx.beginPath();
            ctx.moveTo(s1[0], s1[1]);
            ctx.lineTo(s2[0], s2[1]);
            ctx.strokeStyle = isCorridorWall
                ? '#fb923c'
                : (isSelected ? '#fca5a5' : '#f87171');
            ctx.lineWidth = isSelected ? 3 : 2;
            ctx.setLineDash([8, 4]);
            ctx.stroke();
            ctx.setLineDash([]);

            if (isSelected) {
                [line.p1, line.p2].forEach((pt) => {
                    const scr = toScreen(pt[0], pt[1]);
                    ctx.beginPath();
                    ctx.arc(scr[0], scr[1], 7, 0, 2 * Math.PI);
                    ctx.fillStyle = '#fca5a5';
                    ctx.strokeStyle = '#0f172a';
                    ctx.lineWidth = 1.5;
                    ctx.fill();
                    ctx.stroke();
                });
            }
        });

        if (splitDraftPoints.length > 0) {
            const closeThreshold = Math.max(0.8, 12 / scale);
            const previewPoints =
                splitDraftPoints.length >= 1
                    ? [...splitDraftPoints, snapInfo?.point || dxfMousePos]
                    : splitDraftPoints;

            ctx.beginPath();
            const draftStart = toScreen(previewPoints[0][0], previewPoints[0][1]);
            ctx.moveTo(draftStart[0], draftStart[1]);
            for (let i = 1; i < previewPoints.length; i++) {
                const pt = toScreen(previewPoints[i][0], previewPoints[i][1]);
                ctx.lineTo(pt[0], pt[1]);
            }
            ctx.strokeStyle = 'rgba(248, 113, 113, 0.85)';
            ctx.lineWidth = 2;
            ctx.setLineDash([4, 4]);
            ctx.stroke();
            ctx.setLineDash([]);

            splitDraftPoints.forEach((pt) => {
                const scr = toScreen(pt[0], pt[1]);
                ctx.beginPath();
                ctx.arc(scr[0], scr[1], 5, 0, 2 * Math.PI);
                ctx.fillStyle = '#f87171';
                ctx.fill();
                ctx.strokeStyle = '#0f172a';
                ctx.lineWidth = 1;
                ctx.stroke();
            });

            if (
                splitDraftPoints.length >= 3
                && pointsNearEachOther(
                    splitDraftPoints[0],
                    previewPoints[previewPoints.length - 1],
                    closeThreshold,
                )
            ) {
                const first = toScreen(splitDraftPoints[0][0], splitDraftPoints[0][1]);
                ctx.beginPath();
                ctx.arc(first[0], first[1], 8, 0, 2 * Math.PI);
                ctx.strokeStyle = '#4ade80';
                ctx.lineWidth = 2;
                ctx.stroke();
            }
        }

        if (toolMode === 'corridor' && corridorDraft.length > 0) {
            const previewCenterline = getCorridorPreviewCenterline(true);
            if (previewCenterline.length >= 2) {
                const previewLines = computeCorridorDisplayLines(
                    previewCenterline,
                    corridorWidth,
                    corridorBorderPolygons,
                );
                previewLines.forEach(([p1, p2]) => {
                    const s1 = toScreen(p1[0], p1[1]);
                    const s2 = toScreen(p2[0], p2[1]);
                    ctx.beginPath();
                    ctx.moveTo(s1[0], s1[1]);
                    ctx.lineTo(s2[0], s2[1]);
                    ctx.strokeStyle = 'rgba(251, 146, 60, 0.75)';
                    ctx.lineWidth = 1.5;
                    ctx.setLineDash([6, 4]);
                    ctx.stroke();
                    ctx.setLineDash([]);
                });
            }

            ctx.beginPath();
            const draftStart = toScreen(corridorDraft[0][0], corridorDraft[0][1]);
            ctx.moveTo(draftStart[0], draftStart[1]);
            for (let i = 1; i < corridorDraft.length; i++) {
                const pt = toScreen(corridorDraft[i][0], corridorDraft[i][1]);
                ctx.lineTo(pt[0], pt[1]);
            }
            if (previewCenterline.length > corridorDraft.length) {
                const cursorPt = toScreen(
                    previewCenterline[previewCenterline.length - 1][0],
                    previewCenterline[previewCenterline.length - 1][1],
                );
                ctx.lineTo(cursorPt[0], cursorPt[1]);
            }
            ctx.strokeStyle = '#fb923c';
            ctx.lineWidth = 2;
            ctx.setLineDash([4, 4]);
            ctx.stroke();
            ctx.setLineDash([]);

            corridorDraft.forEach((pt) => {
                const scr = toScreen(pt[0], pt[1]);
                ctx.beginPath();
                ctx.arc(scr[0], scr[1], 5, 0, 2 * Math.PI);
                ctx.fillStyle = '#fb923c';
                ctx.fill();
                ctx.strokeStyle = '#0f172a';
                ctx.lineWidth = 1;
                ctx.stroke();
            });
        }

        if (activeHatch?.origin) {
            const o = toScreen(activeHatch.origin[0], activeHatch.origin[1]);
            ctx.beginPath();
            ctx.arc(o[0], o[1], 6, 0, 2 * Math.PI);
            ctx.fillStyle = '#a78bfa';
            ctx.fill();
            ctx.strokeStyle = '#0f172a';
            ctx.lineWidth = 1.5;
            ctx.stroke();
        }

        if (activeHatch?.angleRef) {
            const r = toScreen(activeHatch.angleRef[0], activeHatch.angleRef[1]);
            ctx.beginPath();
            ctx.arc(r[0], r[1], 5, 0, 2 * Math.PI);
            ctx.fillStyle = '#fbbf24';
            ctx.fill();
            ctx.strokeStyle = '#0f172a';
            ctx.lineWidth = 1;
            ctx.stroke();
        }

        if (activeHatch?.angleRef && (activeHatch?.angle != null || hatchAnglePreview != null)) {
            const displayAngle =
                hatchAngleStep === 'direction' && hatchAnglePreview != null
                    ? hatchAnglePreview
                    : activeHatch.angle;
            const len = 80;
            const rad = (displayAngle * Math.PI) / 180;
            const r = toScreen(activeHatch.angleRef[0], activeHatch.angleRef[1]);
            const ex = r[0] + len * Math.cos(rad);
            const ey = r[1] - len * Math.sin(rad);
            ctx.beginPath();
            ctx.moveTo(r[0], r[1]);
            ctx.lineTo(ex, ey);
            ctx.strokeStyle = '#a78bfa';
            ctx.lineWidth = 2;
            ctx.stroke();
        } else if (
            hatchAngleStep === 'direction' &&
            activeHatch?.angleRef
        ) {
            const r = toScreen(activeHatch.angleRef[0], activeHatch.angleRef[1]);
            ctx.beginPath();
            ctx.moveTo(r[0], r[1]);
            ctx.lineTo(mousePos.x, mousePos.y);
            ctx.strokeStyle = 'rgba(167, 139, 250, 0.7)';
            ctx.lineWidth = 1.5;
            ctx.setLineDash([4, 4]);
            ctx.stroke();
            ctx.setLineDash([]);
        }

        const edgeCount = boundary.length >= 3 ? boundary.length : Math.max(0, boundary.length - 1);

        if (boundary.length >= 2) {
            ctx.beginPath();
            const bStart = toScreen(boundary[0][0], boundary[0][1]);
            ctx.moveTo(bStart[0], bStart[1]);
            for (let i = 1; i < boundary.length; i++) {
                const pt = toScreen(boundary[i][0], boundary[i][1]);
                ctx.lineTo(pt[0], pt[1]);
            }
            if (boundary.length >= 3) {
                ctx.closePath();
                ctx.fillStyle = 'rgba(45, 212, 191, 0.03)';
                ctx.fill();
            }
            ctx.strokeStyle = '#2dd4bf';
            ctx.lineWidth = 2.5;
            ctx.stroke();
        }

        if (rotation && results) {
            const edgeLabels = getUsableEdgeLabels(results);
            ctx.font = 'bold 10px sans-serif';
            edgeLabels.forEach(({ mid, angleDeg }) => {
                const scr = toScreen(mid[0], mid[1]);
                ctx.fillStyle = 'rgba(251, 191, 36, 0.95)';
                ctx.strokeStyle = '#0f172a';
                ctx.lineWidth = 3;
                ctx.strokeText(`${angleDeg}°`, scr[0], scr[1]);
                ctx.fillText(`${angleDeg}°`, scr[0], scr[1]);
            });
        }

        if (boundary.length >= 2 && !rotation) {
            ctx.fillStyle = '#94a3b8';
            ctx.font = '10px sans-serif';
            for (let i = 0; i < edgeCount; i++) {
                const p1 = boundary[i];
                const p2 = boundary[(i + 1) % boundary.length];
                const len = getDistance(p1, p2);
                const mid = [(p1[0] + p2[0]) / 2, (p1[1] + p2[1]) / 2];
                const sMid = toScreen(mid[0], mid[1]);
                const edgeDx = p2[0] - p1[0];
                const edgeDy = p2[1] - p1[1];
                const angle = Math.atan2(edgeDy, edgeDx);
                const tx = sMid[0] + 15 * Math.sin(angle);
                const ty = sMid[1] + 15 * Math.cos(angle);
                ctx.save();
                ctx.translate(tx, ty);
                ctx.rotate(-angle);
                ctx.textAlign = 'center';
                ctx.fillText(`${len.toFixed(1)}m`, 0, 0);
                ctx.restore();
            }
        }

        boundary.forEach((v, index) => {
            const pt = toScreen(v[0], v[1]);
            ctx.beginPath();
            ctx.arc(pt[0], pt[1], 6, 0, 2 * Math.PI);
            ctx.fillStyle = '#2dd4bf';
            ctx.strokeStyle = '#0f172a';
            ctx.lineWidth = 1.5;
            ctx.fill();
            ctx.stroke();
            const distToMouse = Math.hypot(pt[0] - mousePos.x, pt[1] - mousePos.y);
            if (distToMouse < 15) {
                ctx.fillStyle = '#020617';
                ctx.fillRect(pt[0] + 12, pt[1] - 30, 110, 24);
                ctx.strokeStyle = '#334155';
                ctx.strokeRect(pt[0] + 12, pt[1] - 30, 110, 24);
                ctx.fillStyle = '#e2e8f0';
                ctx.fillText(`P${index}: (${v[0].toFixed(1)}, ${v[1].toFixed(1)})`, pt[0] + 18, pt[1] - 14);
            }
        });

        if (results?.mode === 'campus') {
            const drawRing = (ring, style, label) => {
                if (!ring || ring.length < 3) return;
                ctx.beginPath();
                ring.forEach((pt, index) => {
                    const scr = toScreen(pt[0], pt[1]);
                    if (index === 0) ctx.moveTo(scr[0], scr[1]);
                    else ctx.lineTo(scr[0], scr[1]);
                });
                ctx.closePath();
                ctx.fillStyle = style.fill;
                ctx.strokeStyle = style.stroke;
                ctx.lineWidth = 1.5;
                ctx.fill();
                ctx.stroke();
                if (label) {
                    const [cx, cy] = getPolygonCentroid(ring);
                    const scr = toScreen(cx, cy);
                    ctx.fillStyle = style.stroke;
                    ctx.font = 'bold 9px sans-serif';
                    ctx.textAlign = 'center';
                    ctx.textBaseline = 'middle';
                    ctx.fillText(label, scr[0], scr[1]);
                }
            };
            (results.corridors || []).forEach((corridor) => {
                const horizontal = corridor.kind === 'ch';
                drawRing(
                    corridor.boundary,
                    horizontal
                        ? { fill: 'rgba(45, 212, 191, 0.22)', stroke: '#2dd4bf' }
                        : { fill: 'rgba(148, 163, 184, 0.28)', stroke: '#94a3b8' },
                    horizontal ? 'CH' : 'c',
                );
            });
            if (results.central?.boundary) {
                drawRing(results.central.boundary, {
                    fill: 'rgba(167, 139, 250, 0.35)',
                    stroke: '#a78bfa',
                }, 'DH');
            }
        }

        if (results?.groups?.length) {
            const m1Len = m1Params.length || 12.192;
            const m1Wid = m1Params.width || 2.438;
            const groupedM2Count = results.groups.reduce(
                (count, group) => count + (group.module2 || []).length,
                0,
            );
            const m2DrawnSeparately = (displayM2 || []).length === groupedM2Count;
            let m1Flat = 0;
            results.groups.forEach((group, groupIndex) => {
                const accepted = group.accepted;
                const outline = group.outline || [];
                const groupSelected = selectedModuleId === formatGroupSelectionId(groupIndex);
                if (outline.length >= 3) {
                    ctx.beginPath();
                    outline.forEach((pt, index) => {
                        const scr = toScreen(pt[0], pt[1]);
                        if (index === 0) ctx.moveTo(scr[0], scr[1]);
                        else ctx.lineTo(scr[0], scr[1]);
                    });
                    ctx.closePath();
                    ctx.setLineDash(accepted ? [] : [6, 4]);
                    ctx.strokeStyle = groupSelected ? '#e2e8f0' : (accepted ? '#94a3b8' : 'rgba(148, 163, 184, 0.6)');
                    ctx.lineWidth = groupSelected ? 2.5 : (accepted ? 2 : 1);
                    ctx.stroke();
                    ctx.setLineDash([]);
                }
                const m1Alpha = accepted ? 0.35 : 0.16;
                const m2Alpha = accepted ? 0.35 : 0.16;
                (group.module1 || []).forEach((m, index) => {
                    const isSelected = selectedModuleId === formatResultM1ModuleId(m1Flat + index)
                        || draggedResultM1 === m1Flat + index
                        || (results?.mode === 'campus' && groupSelected);
                    drawOrientedBox(ctx, m.x, m.y, m1Len, m1Wid, m.angle, {
                        fill: isSelected ? 'rgba(52, 211, 153, 0.55)' : `rgba(52, 211, 153, ${m1Alpha})`,
                        stroke: isSelected ? '#7dd3fc' : (accepted ? '#34d399' : 'rgba(52, 211, 153, 0.45)'),
                        lineWidth: isSelected ? 2.5 : 1,
                    });
                });
                m1Flat += (group.module1 || []).length;
                if (!m2DrawnSeparately) {
                    (group.module2 || []).forEach((m) => {
                        drawOrientedBox(ctx, m.x, m.y, m2Len, m2Wid, m.angle, {
                            fill: `rgba(236, 72, 153, ${m2Alpha})`,
                            stroke: accepted ? '#ec4899' : 'rgba(236, 72, 153, 0.45)',
                            lineWidth: 1,
                        });
                    });
                }
            });
        } else if (results?.module1) {
            const m1Len = m1Params.length || 12.192;
            const m1Wid = m1Params.width || 2.438;
            results.module1.forEach((m, index) => {
                const resultKey = formatResultM1ModuleId(index);
                const isSelected =
                    selectedModuleId === resultKey || draggedResultM1 === index;
                drawOrientedBox(ctx, m.x, m.y, m1Len, m1Wid, m.angle, {
                    fill: isSelected ? 'rgba(56, 189, 248, 0.5)' : 'rgba(56, 189, 248, 0.3)',
                    stroke: isSelected ? '#7dd3fc' : '#38bdf8',
                    lineWidth: isSelected ? 2.5 : 1,
                });
                const pt = toScreen(m.x, m.y);
                ctx.fillStyle = '#e0f2fe';
                ctx.font = 'bold 9px sans-serif';
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';
                ctx.fillText(results?.mode === 'campus' ? 'DH' : 'M1', pt[0], pt[1]);
            });
        }

        if (manualMode && toolMode === 'select' && results?.strategy_name === 'cluster_m1') {
            const centroid = groupClusterCentroid(results.groups);
            if (centroid) {
                const pt = toScreen(centroid[0], centroid[1]);
                ctx.fillStyle = '#f8fafc';
                ctx.strokeStyle = '#0f172a';
                ctx.lineWidth = 2;
                ctx.fillRect(pt[0] - 6, pt[1] - 6, 12, 12);
                ctx.strokeRect(pt[0] - 6, pt[1] - 6, 12, 12);
            }
        }

        if (manualModules.length > 0) {
            const m1Len = m1Params.length || 12.192;
            const m1Wid = m1Params.width || 2.438;
            manualModules.forEach((m) => {
                const hatchKey = formatHatchModuleId(m.areaId, m.hatchIndex);
                const isDragging =
                    draggedHatchModule?.areaId === m.areaId
                    && draggedHatchModule?.index === m.hatchIndex;
                const isSelected = selectedModuleId === hatchKey || isDragging;
                drawOrientedBox(ctx, m.x, m.y, m1Len, m1Wid, m.angle, {
                    fill: isSelected ? 'rgba(52, 211, 153, 0.55)' : 'rgba(52, 211, 153, 0.35)',
                    stroke: isSelected ? '#6ee7b7' : '#34d399',
                    lineWidth: isSelected ? 2.5 : 1.5,
                });
                const pt = toScreen(m.x, m.y);
                ctx.fillStyle = '#d1fae5';
                ctx.font = 'bold 9px sans-serif';
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';
                ctx.fillText('M1', pt[0], pt[1]);
            });
        }

        (placedModules || []).forEach((m) => {
            const dims = getPlacedModuleDimensions(m.type, m1Params, m2Params, m3Params, m4Params);
            const style = PLACED_MODULE_STYLES[m.type] || PLACED_MODULE_STYLES.M1;
            const isSelected =
                selectedModuleId === m.id ||
                moduleRotation?.moduleId === m.id ||
                draggedPlacedModule === m.id;
            drawOrientedBox(ctx, m.x, m.y, dims.length, dims.width, m.angle, {
                fill: isSelected ? style.fill.replace('0.4', '0.6') : style.fill,
                stroke: isSelected ? '#f8fafc' : style.stroke,
                lineWidth: isSelected ? 2.5 : 1.5,
            });
            const pt = toScreen(m.x, m.y);
            ctx.fillStyle = '#f8fafc';
            ctx.font = 'bold 9px sans-serif';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText(m.type, pt[0], pt[1]);
        });

        if (moduleRotation) {
            const mod = (placedModules || []).find((m) => m.id === moduleRotation.moduleId);
            if (mod) {
                const scr = toScreen(mod.x, mod.y);
                ctx.strokeStyle = '#fbbf24';
                ctx.lineWidth = 1;
                ctx.beginPath();
                ctx.moveTo(scr[0], scr[1]);
                ctx.lineTo(mousePos.x, mousePos.y);
                ctx.stroke();
            }
        }

        if (paletteDragType) {
            const dims = getPlacedModuleDimensions(
                paletteDragType,
                m1Params,
                m2Params,
                m3Params,
                m4Params,
            );
            const style = PLACED_MODULE_STYLES[paletteDragType] || PLACED_MODULE_STYLES.M1;
            const angle =
                activeHatchAreaId && areaHatchConfigs[activeHatchAreaId]?.angle != null
                    ? areaHatchConfigs[activeHatchAreaId].angle
                    : 0;
            const [mx, my] = toDxf(mousePos.x, mousePos.y);
            drawOrientedBox(ctx, mx, my, dims.length, dims.width, angle, {
                fill: style.fill.replace('0.4', '0.25'),
                stroke: style.stroke,
                lineWidth: 1,
            });
        }

        (module3Positions || []).forEach((m, idx) => {
            drawOrientedBox(ctx, m.x, m.y, m3Len, m3Wid, m.angle ?? 0, {
                fill: 'rgba(251, 191, 36, 0.45)',
                stroke: '#fbbf24',
                lineWidth: 1.5,
            });
            const pt = toScreen(m.x, m.y);
            ctx.fillStyle = '#fef3c7';
            ctx.font = 'bold 9px sans-serif';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText('M3', pt[0], pt[1]);
        });

        (module4Positions || []).forEach((m) => {
            drawOrientedBox(ctx, m.x, m.y, m4Len, m4Wid, m.angle ?? 0, {
                fill: 'rgba(96, 165, 250, 0.45)',
                stroke: '#60a5fa',
                lineWidth: 1.5,
            });
            const pt = toScreen(m.x, m.y);
            ctx.fillStyle = '#dbeafe';
            ctx.font = 'bold 9px sans-serif';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText('M4', pt[0], pt[1]);
        });

        if (displayM2) {
            displayM2.forEach((m, idx) => {
                const isActive = draggedM2 === idx || rotation?.m2Idx === idx;
                drawOrientedBox(ctx, m.x, m.y, m2Len, m2Wid, m.angle, {
                    fill: isActive ? 'rgba(236, 72, 153, 0.55)' : 'rgba(236, 72, 153, 0.35)',
                    stroke: isActive ? '#f43f5e' : '#ec4899',
                    lineWidth: 1.5,
                });
                const pt = toScreen(m.x, m.y);
                ctx.fillStyle = '#fdf2f8';
                ctx.font = 'bold 9px sans-serif';
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';
                ctx.fillText(results?.mode === 'campus' ? 'E' : 'M2', pt[0], pt[1]);
            });
        }

        if (invalidM2Preview) {
            drawOrientedBox(
                ctx,
                invalidM2Preview.x,
                invalidM2Preview.y,
                m2Len,
                m2Wid,
                invalidM2Preview.angle,
                { fill: 'rgba(244, 63, 94, 0.2)', stroke: '#f43f5e', lineWidth: 1 },
            );
        }

        if (rotation && displayM2?.[rotation.m2Idx]) {
            const m = displayM2[rotation.m2Idx];
            const scr = toScreen(m.x, m.y);
            ctx.strokeStyle = '#fbbf24';
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(scr[0], scr[1]);
            ctx.lineTo(mousePos.x, mousePos.y);
            ctx.stroke();
        }

        if (snapInfo && !rotation) {
            const spt = toScreen(snapInfo.point[0], snapInfo.point[1]);
            ctx.beginPath();
            ctx.arc(spt[0], spt[1], 8, 0, 2 * Math.PI);
            ctx.lineWidth = 2;
            if (snapInfo.type === 'corner') {
                ctx.strokeStyle = '#22c55e';
                ctx.stroke();
                ctx.strokeRect(spt[0] - 4, spt[1] - 4, 8, 8);
            } else {
                ctx.strokeStyle = '#3b82f6';
                ctx.stroke();
            }
        }
    }, [
        boundary,
        results,
        displayM2,
        scale,
        offset,
        mousePos,
        snapInfo,
        visibleLayers,
        rotation,
        invalidM2Preview,
        usablePolygons,
        hasSplitSubAreas,
        dxfData,
        m1Params,
        m2Len,
        m2Wid,
        draggedM2,
        subAreas,
        selectedAreaIds,
        cutLineDragDraft,
        cutLines,
        corridorWallLines,
        splitDraftPoints,
        corridorDraft,
        corridorWidth,
        dxfMousePos,
        getCorridorPreviewCenterline,
        corridorBorderPolygons,
        activeHatch,
        hatchAngleStep,
        hatchAnglePreview,
        areaHatchConfigs,
        activeHatchAreaId,
        manualModules,
        placedModules,
        selectedCutLineId,
        selectedModuleId,
        moduleRotation,
        draggedPlacedModule,
        draggedHatchModule,
        draggedResultM1,
        paletteDragType,
        manualMode,
        toolMode,
    ]);

    useEffect(() => {
        const handleResize = () => {
            if (!canvasRef.current || !containerRef.current) return;
            const canvas = canvasRef.current;
            const rect = containerRef.current.getBoundingClientRect();
            canvas.width = rect.width;
            canvas.height = rect.height;
            draw();
        };
        window.addEventListener('resize', handleResize);
        handleResize();
        return () => window.removeEventListener('resize', handleResize);
    }, [draw]);

    useEffect(() => {
        draw();
    }, [draw]);

    const getM2Source = () => draftM2List ?? results?.module2;

    const tryUpdateM2Position = (idx, x, y) => {
        const source = getM2Source();
        if (!source) return;
        const mod = source[idx];
        const candidate = { ...mod, x, y };
        if (moduleFitsUsableArea(x, y, m2Len, m2Wid, mod.angle, usablePolygons)) {
            const updated = [...source];
            updated[idx] = candidate;
            setDraftM2List(updated);
            setInvalidM2Preview(null);
        } else {
            setInvalidM2Preview(candidate);
        }
    };

    const tryUpdateM2Angle = (idx, angleDeg) => {
        const source = getM2Source();
        if (!source) return;
        const mod = source[idx];
        const normalized = normalizeAngle360(Math.round(angleDeg));
        const candidate = { ...mod, angle: normalized };
        if (moduleFitsUsableArea(mod.x, mod.y, m2Len, m2Wid, normalized, usablePolygons)) {
            const updated = [...source];
            updated[idx] = candidate;
            setDraftM2List(updated);
            setInvalidM2Preview(null);
            return normalized;
        }
        setInvalidM2Preview(candidate);
        return null;
    };

    const commitRotation = (angleDeg) => {
        if (!rotation || !results?.module2) return;
        const source = draftM2List ?? results.module2;
        const mod = source[rotation.m2Idx];
        const finalAngle = normalizeAngle360(Math.round(angleDeg));
        const updated = [...source];
        updated[rotation.m2Idx] = { ...mod, angle: finalAngle };
        if (!moduleFitsUsableArea(mod.x, mod.y, m2Len, m2Wid, finalAngle, usablePolygons)) {
            setRotation(null);
            setDraftM2List(null);
            setInvalidM2Preview(null);
            return;
        }
        setRotation(null);
        setDraftM2List(null);
        setInvalidM2Preview(null);
        onM2Commit(updated);
    };

    const cancelRotation = () => {
        setRotation(null);
        setDraftM2List(null);
        setInvalidM2Preview(null);
    };

    const handleM2Delete = (idx) => {
        if (!results?.module2) return;
        beginUndoableEdit();
        const updated = results.module2.filter((_, i) => i !== idx);
        setContextMenu(null);
        onM2Commit(updated);
    };

    const handleM2RotateStart = (idx) => {
        if (!results?.module2?.[idx]) return;
        beginUndoableEdit();
        const mod = results.module2[idx];
        pointerStillSinceRef.current = Date.now();
        lastPointerRef.current = null;
        setRotation({
            m2Idx: idx,
            angle: mod.angle,
            latchedAngle: null,
            manualInput: false,
            manualValue: String(Math.round(mod.angle)),
        });
        setDraftM2List([...results.module2]);
        setContextMenu(null);
    };

    const cancelModuleRotation = () => setModuleRotation(null);

    const commitSplitDraft = useCallback(
        (points, closed) => {
            if (!points || points.length < 2) return;
            onCommitDivisionPolyline(points, closed);
            setSplitDraftPoints([]);
        },
        [onCommitDivisionPolyline],
    );

    const tryFinishSplitDraft = useCallback(
        (clickPt, snap) => {
            const closeThreshold = Math.max(0.8, 12 / scale);
            const onBorder = isDivisionBorderPoint(
                clickPt,
                snap,
                corridorBorderPolygons,
                closeThreshold,
            );

            if (
                splitDraftPoints.length >= 3
                && pointsNearEachOther(splitDraftPoints[0], clickPt, closeThreshold)
            ) {
                commitSplitDraft(splitDraftPoints, true);
                return true;
            }

            if (splitDraftPoints.length >= 1 && onBorder) {
                const last = splitDraftPoints[splitDraftPoints.length - 1];
                const finished = pointsNearEachOther(last, clickPt, 0.05)
                    ? splitDraftPoints
                    : [...splitDraftPoints, clickPt];
                if (finished.length >= 2) {
                    commitSplitDraft(finished, false);
                } else {
                    setSplitDraftPoints([]);
                }
                return true;
            }

            return false;
        },
        [splitDraftPoints, scale, corridorBorderPolygons, commitSplitDraft],
    );

    const commitCorridorDraft = useCallback(() => {
        if (corridorDraft.length < 2) return;
        onCommitCorridor(corridorDraft);
        setCorridorDraft([]);
    }, [corridorDraft, onCommitCorridor]);

    const commitModuleRotation = (angleDeg) => {
        if (!moduleRotation) return;
        const finalAngle = normalizeAngle360(Math.round(angleDeg));
        onUpdatePlacedModule(moduleRotation.moduleId, { angle: finalAngle });
        setModuleRotation(null);
    };

    useEffect(() => {
        const onKeyDown = (e) => {
            if (e.key === 'Escape') {
                if (splitDraftPoints.length > 0) {
                    e.preventDefault();
                    setSplitDraftPoints([]);
                }
                if (corridorDraft.length > 0) {
                    e.preventDefault();
                    setCorridorDraft([]);
                }
                if (moduleRotation) {
                    e.preventDefault();
                    cancelModuleRotation();
                }
            }
            if (e.key === 'Enter' && toolMode === 'split' && splitDraftPoints.length >= 2) {
                const last = splitDraftPoints[splitDraftPoints.length - 1];
                if (
                    isDivisionBorderPoint(
                        last,
                        null,
                        corridorBorderPolygons,
                        0.8,
                    )
                ) {
                    e.preventDefault();
                    commitSplitDraft(splitDraftPoints, false);
                }
            }
            if (e.key === 'Enter' && toolMode === 'corridor' && corridorDraft.length >= 2) {
                e.preventDefault();
                commitCorridorDraft();
            }
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [
        splitDraftPoints,
        moduleRotation,
        corridorDraft,
        toolMode,
        commitCorridorDraft,
        commitSplitDraft,
        corridorBorderPolygons,
    ]);

    useEffect(() => {
        if (toolMode !== 'corridor') {
            setCorridorDraft([]);
        }
        if (toolMode !== 'split') {
            setSplitDraftPoints([]);
        }
    }, [toolMode]);

    useEffect(() => {
        if (!manualMode || toolMode !== 'hatchOrigin' || hatchAngleStep !== 'direction') {
            setHatchAnglePreview(null);
            return undefined;
        }
        const onKeyDown = (e) => {
            if (e.key !== 'Tab') return;
            if (!activeHatchAreaId) return;
            const cfg = areaHatchConfigs[activeHatchAreaId];
            if (cfg?.angle == null) return;
            e.preventDefault();
            setHatchAngleStep(null);
            setToolMode('select');
            setHatchAnglePreview(null);
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [manualMode, toolMode, hatchAngleStep, activeHatchAreaId, areaHatchConfigs, setHatchAngleStep, setToolMode]);

    useEffect(() => {
        if (!paletteDragType) return undefined;
        const onUp = () => {
            if (!canvasRef.current) {
                setPaletteDragType(null);
                return;
            }
            const rect = canvasRef.current.getBoundingClientRect();
            const sx = mousePosRef.current.x;
            const sy = mousePosRef.current.y;
            if (sx < 0 || sy < 0 || sx > rect.width || sy > rect.height) {
                setPaletteDragType(null);
                return;
            }
            const x = (sx - offset.x) / scale;
            const y = (offset.y - sy) / scale;
            const clickPt = [x, y];
            const angle =
                activeHatchAreaId && areaHatchConfigs[activeHatchAreaId]?.angle != null
                    ? areaHatchConfigs[activeHatchAreaId].angle
                    : 0;
            const dims = getPlacedModuleDimensions(
                paletteDragType,
                m1Params,
                m2Params,
                m3Params,
                m4Params,
            );
            if (
                moduleFitsUsableArea(
                    clickPt[0],
                    clickPt[1],
                    dims.length,
                    dims.width,
                    angle,
                    usablePolygons,
                )
            ) {
                if (paletteDragType === 'M3' || paletteDragType === 'M4') {
                    onInfraPlace?.(paletteDragType, clickPt[0], clickPt[1], angle);
                } else {
                    onPlaceModule(paletteDragType, clickPt[0], clickPt[1], angle);
                }
            }
            setPaletteDragType(null);
        };
        window.addEventListener('mouseup', onUp);
        return () => window.removeEventListener('mouseup', onUp);
    }, [
        paletteDragType,
        areaHatchConfigs,
        activeHatchAreaId,
        m1Params,
        m2Params,
        m3Params,
        m4Params,
        module3Positions,
        module4Positions,
        usablePolygons,
        onPlaceModule,
        onInfraPlace,
        setPaletteDragType,
        scale,
        offset,
    ]);

    useEffect(() => {
        if (!rotation) return undefined;
        const onKeyDown = (e) => {
            if (e.key === 'Escape') {
                e.preventDefault();
                cancelRotation();
            } else if (e.key === 'Tab') {
                e.preventDefault();
                setRotation((prev) => ({ ...prev, manualInput: true }));
                setTimeout(() => rotationInputRef.current?.focus(), 0);
            } else if (e.key === 'Enter' && rotation.manualInput) {
                e.preventDefault();
                const val = parseFloat(rotation.manualValue);
                if (!Number.isNaN(val)) commitRotation(val);
            }
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [rotation]);

    const handleMouseDown = (e) => {
        if (contextMenu) setContextMenu(null);

        const rect = canvasRef.current.getBoundingClientRect();
        const sx = e.clientX - rect.left;
        const sy = e.clientY - rect.top;
        const [mx, my] = toDxf(sx, sy);

        if (moduleRotation) {
            if (e.button === 0) {
                const mod = (placedModules || []).find((m) => m.id === moduleRotation.moduleId);
                if (mod) commitModuleRotation(mod.angle);
            }
            return;
        }

        if (rotation) {
            if (e.button === 0 && !rotation.manualInput) {
                const mod = (draftM2List ?? results.module2)[rotation.m2Idx];
                commitRotation(mod.angle);
            }
            return;
        }

        if (manualMode && e.button === 2 && toolMode === 'split' && splitDraftPoints.length > 0) {
            e.preventDefault();
            setSplitDraftPoints((prev) => prev.slice(0, -1));
            return;
        }

        if (manualMode && e.button === 2 && toolMode === 'corridor' && corridorDraft.length > 0) {
            e.preventDefault();
            setCorridorDraft((prev) => prev.slice(0, -1));
            return;
        }

        if (manualMode && e.button === 0) {
            const clickPt = snapInfo ? snapInfo.point : [mx, my];
            const hitThreshold = Math.max(0.5, 12 / scale);

            if (toolMode === 'corridor') {
                if (e.detail >= 2) {
                    if (corridorDraft.length >= 2) {
                        commitCorridorDraft();
                    }
                    return;
                }
                setCorridorDraft((prev) => [...prev, clickPt]);
                return;
            }

            if (toolMode === 'split') {
                if (e.detail >= 2) {
                    if (splitDraftPoints.length >= 2) {
                        const last = splitDraftPoints[splitDraftPoints.length - 1];
                        if (
                            isDivisionBorderPoint(
                                last,
                                snapInfo,
                                corridorBorderPolygons,
                                Math.max(0.8, 12 / scale),
                            )
                        ) {
                            commitSplitDraft(splitDraftPoints, false);
                        }
                    }
                    return;
                }
                if (tryFinishSplitDraft(clickPt, snapInfo)) {
                    return;
                }
                setSplitDraftPoints((prev) => [...prev, clickPt]);
                return;
            }

            if (toolMode === 'select') {
                const selectedLine = getSelectedCutLine();
                if (selectedLine) {
                    const node = hitTestCutLineNode(
                        [mx, my],
                        selectedLine,
                        hitThreshold,
                    );
                    if (node) {
                        beginUndoableEdit();
                        setDraggedCutNode({ lineId: selectedLine.id, node });
                        setCutLineDragDraft({
                            lineId: selectedLine.id,
                            p1: [...selectedLine.p1],
                            p2: [...selectedLine.p2],
                        });
                        return;
                    }
                }

                const placedId = hitTestPlacedModules(
                    [mx, my],
                    placedModules,
                    m1Params,
                    m2Params,
                );
                if (placedId) {
                    setSelectedModuleId(placedId);
                    setSelectedCutLineId(null);
                    if (!e.shiftKey) setSelectedAreaIds([]);
                    beginUndoableEdit();
                    setDraggedPlacedModule(placedId);
                    return;
                }

                const hatchHit = hitTestHatchModules([mx, my], areaLayouts, m1Params);
                if (hatchHit) {
                    setSelectedModuleId(formatHatchModuleId(hatchHit.areaId, hatchHit.index));
                    setSelectedCutLineId(null);
                    setSelectedAreaIds([]);
                    beginUndoableEdit();
                    setDraggedHatchModule({ areaId: hatchHit.areaId, index: hatchHit.index });
                    return;
                }

                if (results?.strategy_name === 'cluster_m1') {
                    const centroid = groupClusterCentroid(results.groups);
                    if (centroid && Math.hypot(mx - centroid[0], my - centroid[1]) <= 2.5) {
                        beginUndoableEdit();
                        clusterDragRef.current = {
                            origin: [mx, my],
                            snapshots: JSON.parse(JSON.stringify(results.groups)),
                        };
                        setSelectedCutLineId(null);
                        setSelectedAreaIds([]);
                        return;
                    }
                }

                if (results?.mode === 'campus') {
                    const campusIdx = hitTestCampusGroup([mx, my], results, m1Params, m2Params);
                    if (campusIdx !== -1) {
                        beginUndoableEdit();
                        groupDragRef.current = {
                            groupIndex: campusIdx,
                            origin: [mx, my],
                            snapshot: JSON.parse(JSON.stringify(results.groups[campusIdx])),
                            last: null,
                        };
                        setSelectedModuleId(formatGroupSelectionId(campusIdx));
                        setSelectedCutLineId(null);
                        setSelectedAreaIds([]);
                        return;
                    }
                } else {
                    const resultM1Idx = hitTestResultM1Modules([mx, my], results, m1Params);
                    if (resultM1Idx !== -1) {
                        setSelectedModuleId(formatResultM1ModuleId(resultM1Idx));
                        setSelectedCutLineId(null);
                        setSelectedAreaIds([]);
                        beginUndoableEdit();
                        setDraggedResultM1(resultM1Idx);
                        return;
                    }

                    if (results?.module2) {
                        const m2Idx = hitTestM2Modules([mx, my], displayM2, m2Len, m2Wid);
                        if (m2Idx !== -1) {
                            setSelectedModuleId(formatResultM2ModuleId(m2Idx));
                            setSelectedCutLineId(null);
                            setSelectedAreaIds([]);
                            beginUndoableEdit();
                            setDraggedM2(m2Idx);
                            setDraftM2List([...(draftM2List ?? results.module2)]);
                            return;
                        }
                    }
                }

                const groupIdx = hitTestGroupOutlines([mx, my], results?.groups);
                if (groupIdx !== -1) {
                    beginUndoableEdit();
                    groupDragRef.current = {
                        groupIndex: groupIdx,
                        origin: [mx, my],
                        snapshot: JSON.parse(JSON.stringify(results.groups[groupIdx])),
                    };
                    setSelectedModuleId(formatGroupSelectionId(groupIdx));
                    setSelectedCutLineId(null);
                    setSelectedAreaIds([]);
                    return;
                }

                const cutId = hitTestCutLines([mx, my], cutLines, hitThreshold);
                if (cutId) {
                    setSelectedCutLineId(cutId);
                    setSelectedModuleId(null);
                    setSelectedAreaIds([]);
                    return;
                }

                const areaId = hitTestSubAreas([mx, my], subAreas);
                if (areaId) {
                    setSelectedCutLineId(null);
                    setSelectedModuleId(null);
                    setSelectedAreaIds((prev) =>
                        updateAreaSelection(prev, areaId, e.shiftKey),
                    );
                    return;
                }
                return;
            }

            if (toolMode === 'hatchOrigin') {
                if (!activeHatchAreaId) {
                    setError?.('Select exactly one area before setting hatch origin.');
                    return;
                }
                beginUndoableEdit();
                if (!hatchAngleStep) {
                    updateAreaHatchConfig(activeHatchAreaId, { origin: clickPt });
                    const { nextStep, patch } = getHatchStepAfterOrigin(activeHatch, clickPt);
                    if (Object.keys(patch).length > 0) {
                        updateAreaHatchConfig(activeHatchAreaId, patch);
                    }
                    setHatchAngleStep(nextStep);
                    return;
                }
                if (hatchAngleStep === 'reference') {
                    updateAreaHatchConfig(activeHatchAreaId, { angleRef: clickPt });
                    setHatchAngleStep('direction');
                    return;
                }
                if (hatchAngleStep === 'direction' && activeHatch?.angleRef) {
                    const rawAngle = pointerToModuleAngle(
                        clickPt[0],
                        clickPt[1],
                        activeHatch.angleRef[0],
                        activeHatch.angleRef[1],
                    );
                    saveSnappedHatchAngle(activeHatchAreaId, rawAngle, activeHatch.angleRef);
                    setHatchAngleStep(null);
                    setHatchAnglePreview(null);
                    setToolMode('select');
                    return;
                }
                return;
            }
        }

        if (manualMode && e.button === 0) {
            return;
        }

        if (e.button === 2 && results?.module2) {
            const hitIdx = hitTestM2Modules([mx, my], displayM2, m2Len, m2Wid);
            if (hitIdx !== -1) {
                e.preventDefault();
                setContextMenu({
                    m2Idx: hitIdx,
                    screenX: e.clientX,
                    screenY: e.clientY,
                });
                return;
            }
        }

        if (e.button === 0 && e.altKey) {
            const clickedIdx = boundary.findIndex((v) => getDistance([mx, my], v) * scale < 15);
            if (clickedIdx !== -1) {
                beginUndoableEdit();
                setBoundary(boundary.filter((_, idx) => idx !== clickedIdx));
                return;
            }
        }

        if (e.button === 0 && !manualMode) {
            if (displayM2 && results) {
                const clickedM2Idx = hitTestM2Modules([mx, my], displayM2, m2Len, m2Wid);
                if (clickedM2Idx !== -1) {
                    beginUndoableEdit();
                    setDraggedM2(clickedM2Idx);
                    setDraftM2List([...(draftM2List ?? results.module2)]);
                    return;
                }
            }

            const snap = getSnapPoint(mx, my);
            const clickPt = snap ? snap.point : [mx, my];
            const hitThreshold = 12 / scale;

            const clickedVertexIdx = boundary.findIndex(
                (v) => getDistance(clickPt, v) < hitThreshold,
            );
            if (clickedVertexIdx !== -1) {
                beginUndoableEdit();
                setDraggedVertex(clickedVertexIdx);
                return;
            }

            const edgeInsert = findBoundaryEdgeInsertion(
                clickPt,
                boundary,
                hitThreshold,
                hitThreshold * 0.5,
            );
            if (edgeInsert) {
                beginUndoableEdit();
                const newBoundary = [...boundary];
                newBoundary.splice(edgeInsert.edgeIndex + 1, 0, edgeInsert.point);
                setBoundary(newBoundary);
                setDraggedVertex(edgeInsert.edgeIndex + 1);
                return;
            }

            beginUndoableEdit();
            setBoundary([...boundary, clickPt]);
        }

        if (e.button === 1 || (e.button === 2 && !contextMenu)) {
            setIsPanning(true);
            setPanStart({ x: e.clientX, y: e.clientY });
        }
    };

    const handleMouseMove = (e) => {
        const rect = canvasRef.current.getBoundingClientRect();
        const sx = e.clientX - rect.left;
        const sy = e.clientY - rect.top;
        setMousePos({ x: sx, y: sy });
        mousePosRef.current = { x: sx, y: sy };
        const [mx, my] = toDxf(sx, sy);
        setDxfMousePos([mx, my]);

        if (moduleRotation) {
            const mod = (placedModules || []).find((m) => m.id === moduleRotation.moduleId);
            if (!mod) return;
            const rawAngle = pointerToModuleAngle(mx, my, mod.x, mod.y);
            const snapped = snapAngleToEdges(rawAngle, snapEdgeAngles, 2);
            const applied = normalizeAngle360(Math.round(snapped));
            onUpdatePlacedModule(moduleRotation.moduleId, { angle: applied });
            setModuleRotation((prev) => ({ ...prev, angle: applied }));
            return;
        }

        if (
            manualMode
            && toolMode === 'hatchOrigin'
            && hatchAngleStep === 'direction'
            && activeHatch?.angleRef
        ) {
            const rawAngle = pointerToModuleAngle(
                mx,
                my,
                activeHatch.angleRef[0],
                activeHatch.angleRef[1],
            );
            const snapped = snapAngleToEdges(rawAngle, snapEdgeAngles, 2);
            setHatchAnglePreview(normalizeAngle360(snapped));
            return;
        }

        if (rotation && !rotation.manualInput) {
            const mod = (draftM2List ?? results?.module2)?.[rotation.m2Idx];
            if (!mod) return;

            const pointerKey = `${mx.toFixed(2)},${my.toFixed(2)}`;
            if (lastPointerRef.current !== pointerKey) {
                lastPointerRef.current = pointerKey;
                pointerStillSinceRef.current = Date.now();
            }
            const stillMs = Date.now() - pointerStillSinceRef.current;
            const rawAngle = pointerToModuleAngle(mx, my, mod.x, mod.y);
            const { angle, latched } = snapAngleWithStillThreshold(
                rawAngle,
                rotation.latchedAngle,
                stillMs,
            );
            const applied = tryUpdateM2Angle(rotation.m2Idx, angle);
            if (applied !== null) {
                setRotation((prev) => ({
                    ...prev,
                    angle: applied,
                    latchedAngle: latched,
                    manualValue: String(applied),
                }));
            }
            return;
        }

        const snap = getSnapPoint(mx, my);
        setSnapInfo(snap);
        snapInfoRef.current = snap;
        const targetCoords = snap ? snap.point : [mx, my];

        if (draggedCutNode && cutLineDragDraft) {
            const pt = snapInfo ? snapInfo.point : [mx, my];
            setCutLineDragDraft((prev) => {
                if (!prev) return prev;
                if (draggedCutNode.node === 'p1') {
                    return { ...prev, p1: pt };
                }
                return { ...prev, p2: pt };
            });
            return;
        }

        if (draggedPlacedModule) {
            const pt = [mx, my];
            const mod = (placedModules || []).find((m) => m.id === draggedPlacedModule);
            if (mod) {
                const dims = getPlacedModuleDimensions(mod.type, m1Params, m2Params);
                if (
                    moduleFitsUsableArea(
                        pt[0],
                        pt[1],
                        dims.length,
                        dims.width,
                        mod.angle,
                        usablePolygons,
                    )
                ) {
                    onUpdatePlacedModule(draggedPlacedModule, { x: pt[0], y: pt[1] });
                }
            }
            return;
        }

        if (draggedHatchModule) {
            const pt = [mx, my];
            const { areaId, index } = draggedHatchModule;
            const mod = areaLayouts?.[areaId]?.module1?.[index];
            const m1Len = m1Params.length || 12.192;
            const m1Wid = m1Params.width || 2.438;
            if (
                mod
                && moduleFitsUsableArea(pt[0], pt[1], m1Len, m1Wid, mod.angle, usablePolygons)
            ) {
                onUpdateHatchModule(areaId, index, { x: pt[0], y: pt[1] });
            }
            return;
        }

        if (draggedResultM1 != null) {
            const pt = [mx, my];
            const mod = results?.module1?.[draggedResultM1];
            const m1Len = m1Params.length || 12.192;
            const m1Wid = m1Params.width || 2.438;
            if (
                mod
                && moduleFitsUsableArea(pt[0], pt[1], m1Len, m1Wid, mod.angle, usablePolygons)
            ) {
                onUpdateResultM1(draggedResultM1, { x: pt[0], y: pt[1] });
            }
            return;
        }

        if (clusterDragRef.current) {
            const { origin, snapshots } = clusterDragRef.current;
            const dx = mx - origin[0];
            const dy = my - origin[1];
            const m1Len = m1Params.length || 12.192;
            const m1Wid = m1Params.width || 2.438;
            const fits = snapshots.every((group) => (
                (group.module1 || []).every((mod) => moduleFitsUsableArea(
                    mod.x + dx, mod.y + dy, m1Len, m1Wid, mod.angle, usablePolygons,
                ))
                && (group.module2 || []).every((mod) => moduleFitsUsableArea(
                    mod.x + dx, mod.y + dy, m2Len, m2Wid, mod.angle, usablePolygons,
                ))
            ));
            if (fits) onTranslateCluster?.(dx, dy, snapshots);
            return;
        }

        if (groupDragRef.current) {
            const { groupIndex, origin, snapshot } = groupDragRef.current;
            const dx = mx - origin[0];
            const dy = my - origin[1];
            const m1Len = m1Params.length || 12.192;
            const m1Wid = m1Params.width || 2.438;
            const m1Ok = (snapshot.module1 || []).every((mod) => moduleFitsUsableArea(
                mod.x + dx, mod.y + dy, m1Len, m1Wid, mod.angle, usablePolygons,
            ));
            const campus = results?.mode === 'campus';
            const fits = campus
                ? campusRigidFits(snapshot, dx, dy, m1Len, m1Wid, usablePolygons)
                : m1Ok && (snapshot.module2 || []).every((mod) => moduleFitsUsableArea(
                    mod.x + dx, mod.y + dy, m2Len, m2Wid, mod.angle, usablePolygons,
                ));
            if (fits) {
                groupDragRef.current.last = { dx, dy };
                onTranslateGroup?.(groupIndex, dx, dy, snapshot);
            }
            return;
        }

        if (draggedVertex !== null && !manualMode) {
            const newBoundary = [...boundary];
            newBoundary[draggedVertex] = targetCoords;
            setBoundary(newBoundary);
        } else if (draggedM2 !== null) {
            tryUpdateM2Position(draggedM2, targetCoords[0], targetCoords[1]);
        } else if (isPanning) {
            const pdx = e.clientX - panStart.x;
            const pdy = e.clientY - panStart.y;
            setOffset((prev) => ({ x: prev.x + pdx, y: prev.y + pdy }));
            setPanStart({ x: e.clientX, y: e.clientY });
        }
    };

    const handleMouseUp = () => {
        if (draggedCutNode && cutLineDragDraft) {
            onUpdateCutLine(
                cutLineDragDraft.lineId,
                cutLineDragDraft.p1,
                cutLineDragDraft.p2,
                true,
            );
            setDraggedCutNode(null);
            setCutLineDragDraft(null);
        }
        if (draggedPlacedModule) {
            setDraggedPlacedModule(null);
        }
        if (draggedHatchModule) {
            setDraggedHatchModule(null);
        }
        if (draggedResultM1 != null) {
            setDraggedResultM1(null);
        }
        const finishedGroupDrag = groupDragRef.current;
        groupDragRef.current = null;
        if (finishedGroupDrag?.last && results?.mode === 'campus') {
            onRefitCampus?.(
                finishedGroupDrag.groupIndex,
                finishedGroupDrag.last.dx,
                finishedGroupDrag.last.dy,
                finishedGroupDrag.snapshot,
            );
        }
        clusterDragRef.current = null;
        if (draggedM2 !== null && draftM2List) {
            onM2Commit(draftM2List);
        }
        setDraggedVertex(null);
        setDraggedM2(null);
        setDraftM2List(null);
        setInvalidM2Preview(null);
        setIsPanning(false);
    };

    const handleContextMenu = (e) => {
        e.preventDefault();
        const rect = canvasRef.current.getBoundingClientRect();
        const sx = e.clientX - rect.left;
        const sy = e.clientY - rect.top;
        const [mx, my] = toDxf(sx, sy);

        if (manualMode && toolMode === 'split' && splitDraftPoints.length > 0) {
            setSplitDraftPoints([]);
            return;
        }

        if (manualMode && toolMode === 'corridor' && corridorDraft.length > 0) {
            setCorridorDraft([]);
            return;
        }

        if (manualMode) {
            const placedId = hitTestPlacedModules(
                [mx, my],
                placedModules,
                m1Params,
                m2Params,
            );
            if (placedId) {
                const mod = (placedModules || []).find((m) => m.id === placedId);
                if (mod) {
                    beginUndoableEdit();
                    setSelectedModuleId(placedId);
                    setSelectedCutLineId(null);
                    setSelectedAreaIds([]);
                    setModuleRotation({ moduleId: placedId, angle: mod.angle });
                }
                return;
            }
        }

        if (results?.module2) {
            const hitIdx = hitTestM2Modules([mx, my], displayM2, m2Len, m2Wid);
            if (hitIdx !== -1) {
                setContextMenu({ m2Idx: hitIdx, screenX: e.clientX, screenY: e.clientY });
                return;
            }
        }

        if (e.altKey) {
            const clickedIdx = boundary.findIndex((v) => getDistance([mx, my], v) * scale < 15);
            if (clickedIdx !== -1) {
                beginUndoableEdit();
                setBoundary(boundary.filter((_, idx) => idx !== clickedIdx));
            }
        }
    };

    const handleWheel = (e) => {
        e.preventDefault();
        const rect = canvasRef.current.getBoundingClientRect();
        const sx = e.clientX - rect.left;
        const sy = e.clientY - rect.top;
        const [mx, my] = toDxf(sx, sy);
        const zoomFactor = 1.15;
        const newScale = e.deltaY < 0 ? scale * zoomFactor : scale / zoomFactor;
        if (newScale < 0.05 || newScale > 200) return;
        setScale(newScale);
        setOffset({ x: sx - mx * newScale, y: sy + my * newScale });
    };

    return (
        <div ref={containerRef} className="sp-viewport-root">
            <div className="sp-viewport-mode-toggle">
                <button
                    type="button"
                    className={`sp-manual-toggle${manualMode ? ' on' : ''}`}
                    onClick={() => onManualModeChange(!manualMode)}
                >
                    <PenTool size={14} />
                    Manual Mode {manualMode ? 'ON' : 'OFF'}
                </button>
                {manualMode && (
                    <div className="sp-manual-tool-hint">
                        {toolMode === 'split' &&
                            'Click to chain division points · finish on border or close on first point · Esc cancels'}
                        {toolMode === 'corridor' &&
                            'Click centerline points (snapped) · Enter/double-click finishes · Esc cancels'}
                        {toolMode === 'select' &&
                            'Select areas, cut lines, or modules · Drag modules to reposition · Right-click module to rotate'}
                        {toolMode === 'hatchOrigin' &&
                            (hatchAngleStep === 'reference'
                                ? 'Click angle reference point'
                                : hatchAngleStep === 'direction'
                                  ? 'Adjust hatch angle · Tab keeps current angle'
                                  : 'Select one area, then click hatch origin')}
                    </div>
                )}
            </div>

            <canvas
                ref={canvasRef}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
                onContextMenu={handleContextMenu}
                onWheel={handleWheel}
                className={`sp-canvas${rotation ? ' sp-canvas-rotating' : ''}`}
            />

            {contextMenu && (
                <div
                    className="sp-context-menu"
                    style={{ left: contextMenu.screenX, top: contextMenu.screenY }}
                >
                    <button type="button" onClick={() => handleM2RotateStart(contextMenu.m2Idx)}>
                        Rotate
                    </button>
                    <button
                        type="button"
                        className="sp-context-menu-danger"
                        onClick={() => handleM2Delete(contextMenu.m2Idx)}
                    >
                        Delete
                    </button>
                </div>
            )}

            {manualMode && toolMode === 'hatchOrigin' && hatchAngleStep === 'direction' && (
                <div className="sp-rotation-hud">
                    <span className="sp-rotation-angle">
                        {Math.round(hatchAnglePreview ?? activeHatch?.angle ?? 0)}°
                    </span>
                    <span className="sp-rotation-hint">
                        {activeHatch?.angle != null
                            ? 'Tab: keep angle · Click: confirm new angle'
                            : 'Click to set hatch angle'}
                    </span>
                </div>
            )}

            {moduleRotation && (
                <div className="sp-rotation-hud">
                    <span className="sp-rotation-angle">
                        {Math.round(moduleRotation.angle ?? 0)}°
                    </span>
                    <span className="sp-rotation-hint">
                        Right-click started · Click to confirm · Esc to cancel
                    </span>
                </div>
            )}

            {rotation && (
                <div className="sp-rotation-hud">
                    {rotation.manualInput ? (
                        <input
                            ref={rotationInputRef}
                            type="number"
                            className="sp-rotation-input"
                            value={rotation.manualValue}
                            onChange={(e) =>
                                setRotation((prev) => ({ ...prev, manualValue: e.target.value }))
                            }
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    const val = parseFloat(rotation.manualValue);
                                    if (!Number.isNaN(val)) commitRotation(val);
                                }
                            }}
                        />
                    ) : (
                        <span className="sp-rotation-angle">{Math.round(rotation.angle)}°</span>
                    )}
                    <span className="sp-rotation-hint">Tab: type angle · Click: confirm · Esc: cancel</span>
                </div>
            )}

            <div className="sp-info-overlay">
                <div>
                    Scale: <span className="sp-highlight">{Math.round(scale * 100)}%</span>
                </div>
                <div>
                    Cursor:{' '}
                    <span className="sp-highlight">
                        {dxfMousePos[0].toFixed(1)}m, {dxfMousePos[1].toFixed(1)}m
                    </span>
                </div>
                {boundary.length > 0 && (
                    <div>
                        Boundary: <span className="sp-highlight">{boundary.length} vertices</span>
                    </div>
                )}
                <div>
                    Undo: <span className="sp-highlight">Ctrl+Z</span>
                </div>
            </div>

            <div className="sp-viewport-tools">
                <button type="button" onClick={() => setScale((prev) => prev * 1.25)} className="sp-tool-btn">
                    <ZoomIn size={16} />
                </button>
                <button type="button" onClick={() => setScale((prev) => prev / 1.25)} className="sp-tool-btn">
                    <ZoomOut size={16} />
                </button>
                <button type="button" onClick={resetView} className="sp-tool-btn">
                    <Maximize2 size={16} />
                </button>
            </div>
        </div>
    );
}
